/**
 * La gráfica de tráfico, compartida por Analíticas y por el resumen del panel.
 *
 * Vivía dentro de `AnalyticsSection`. El resumen tenía su propia versión
 * reducida, que fue quedándose atrás: sin comparación, sin tooltip de dos
 * series y con otra escala. Dos dibujos del mismo dato que no coinciden hacen
 * dudar de los dos, así que ahora hay uno solo y el resumen le pasa `compact`.
 *
 * El dibujo en sí ya no está aquí: es `MetricChart`, que no sabe nada de
 * vistas. Esto es sólo la capa que le pone nombre a las dos métricas del
 * tráfico y traduce la serie que devuelve la API.
 */
import { MetricChart, type MetricPoint } from './MetricChart'
import { useI18n } from '../../lib/i18n'
import type { ProjectAnalytics } from '../../lib/api'
import { IconChartBar } from '@tabler/icons-react'

/** Vistas en la línea, únicos en el tooltip. Ver `MetricPoint`. */
function toPoints(series: { day: string; hour?: number; views: number; uniques: number }[]): MetricPoint[] {
  return series.map((point) => ({
    day: point.day,
    ...(point.hour === undefined ? {} : { hour: point.hour }),
    primary: point.views,
    secondary: point.uniques,
  }))
}

export function TrafficChart({ series, comparison = [], comparisonLabel = null, granularity, timezone, compact = false, loading = false }: {
  series: ProjectAnalytics['series']
  /** Serie del tramo comparado, alineada por posición con `series`. */
  comparison?: { day: string; views: number; uniques: number }[]
  comparisonLabel?: string | null
  /** Versión para un widget del panel: más baja, sin marco ni cabecera. */
  compact?: boolean
  granularity?: ProjectAnalytics['granularity']
  timezone?: string | undefined
  loading?: boolean
}) {
  const { t } = useI18n()
  const hourly = granularity === 'hour'
  return (
    <MetricChart
      series={toPoints(series)}
      comparison={toPoints(comparison)}
      comparisonLabel={comparisonLabel}
      granularity={granularity}
      timezone={timezone}
      compact={compact}
      loading={loading}
      title={t('traffic.title')}
      subtitle={hourly ? t('traffic.perHour') : t('traffic.perDay')}
      icon={<IconChartBar size={14} />}
      primaryLabel={t('traffic.views')}
      /* Los únicos no se cuentan por hora —eso exige deduplicar visitantes en
         cada tramo—, así que en la curva intradía su columna no existe. */
      secondaryLabel={hourly ? null : t('traffic.uniques')}
    />
  )
}
